// A visit form the forms agent's assistant submitted for the patient, unsigned
// (docs/superpowers/specs/2026-10-30-forms-agent-conversation-design.md), as the forms window marks
// it: a badge (how much of the form's required part is answered), the answers it filled in through
// the agent, each "(AI Assisted)", and Ask buttons that open the agent's chat with a question about
// an answer filled in. The question travels in the navigation's state, never in the address, since it
// holds the patient's answers; the agent finds the follow-up by its reference (`ref`, form_answers).
// Someone who isn't a member of the agent is told who runs it instead.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// The character the badge uses that isn't ASCII, so this file stays ASCII.
const DOT = String.fromCharCode(0x00b7);

const BOX = {
  marginTop: '6px', padding: '6px 8px', borderRadius: '4px', fontSize: '12px',
  backgroundColor: '#f5f3ff', border: '1px solid #ddd6fe', color: '#4c1d95',
};
const LINKISH = {
  marginLeft: '8px', padding: 0, border: 'none', background: 'none', color: '#6d28d9',
  textDecoration: 'underline', cursor: 'pointer', fontSize: '12px',
};

const shown = (value) => (Array.isArray(value) ? value.join(', ') : String(value ?? ''));

export function badgeText(info) {
  const counted = Number.isFinite(info?.answered) && Number.isFinite(info?.total) && info.total > 0;
  return `Submitted by AI assistant ${DOT} not signed${counted ? ` ${DOT} ${info.answered} of ${info.total} answered` : ''}`;
}

export function askAbout(info, answer = null) {
  const head = `About the visit form in follow-up ${info.ref}:`;
  if (!answer) return head;
  return `${head} why did you answer "${answer.label}" with "${shown(answer.value)}"? `
    + 'What did the patient say about it, exactly?';
}

export default function AssistedFormBadge({ info }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (!info) return null;
  const answers = Array.isArray(info.assisted) ? info.assisted : [];
  const ask = (answer) => navigate(`/agents?agent=${encodeURIComponent(info.agent_id)}`,
    { state: { ask: askAbout(info, answer) } });
  return (
    <div style={BOX}>
      <span style={{ fontWeight: 600 }}>{badgeText(info)}</span>
      {info.can_ask ? (
        <button type="button" style={LINKISH} onClick={() => ask(null)}>Ask the agent</button>
      ) : (
        <span style={{ marginLeft: '8px' }}>{`Ask ${info.runs_it || "the agent's owner"} about these answers.`}</span>
      )}
      {answers.length > 0 && (
        <button type="button" style={LINKISH} aria-expanded={open} onClick={() => setOpen((was) => !was)}>
          {open ? 'Hide answers' : 'Show answers'}
        </button>
      )}
      {open && (
        <ul style={{ margin: '6px 0 0 16px', padding: 0, listStyleType: 'disc' }}>
          {answers.map((answer) => (
            <li key={answer.field}>
              <span>{`${answer.label || answer.field}: ${shown(answer.value)} (AI Assisted)`}</span>
              {info.can_ask && (
                <button type="button" style={LINKISH} aria-label={`Ask about ${answer.label || answer.field}`}
                        onClick={() => ask(answer)}>
                  Ask
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
